import React from 'react';
import { Button, Dropdown, InputGroup, Form, Card, Row, Col, Badge } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faCog, faSearch } from '@fortawesome/free-solid-svg-icons';

/**
 * MobileListLayout - Componente riutilizzabile per il layout mobile delle liste
 * 
 * Gestisce:
 * - Toolbar con pulsante creazione e dropdown azioni
 * - Filtri rapidi configurabili (opzionali)
 * - Search bar con contatore filtri attivi
 * - Layout a card personalizzabile
 * - Sticky action bar per selezioni multiple
 */

const MobileListLayout = ({
  // Configurazione entità
  entityName = 'elemento',
  entityNamePlural = 'elementi',
  
  // Toolbar
  createPath,
  createButtonText,
  createButtonIcon = faPlus,
  actionsDropdownContent = null,
  
  // Filtri rapidi (opzionale)
  quickFilters = null, // Array di { label, icon, value, isActive, onClick, variant, gradientActive, gradientInactive }
  
  // Search
  searchPlaceholder = 'Ricerca...',
  searchValue = '',
  activeFiltersCount = 0,
  onSearchChange,
  
  // Dati
  items = [],
  
  // Card rendering
  renderCard, // Funzione (item, isSelected, onToggleSelect) => ReactNode
  onCardClick, // Funzione (item) => void
  
  // Selezione
  selectedIds = [],
  onToggleSelect, // Funzione (id) => void
  onSelectAll, // Funzione () => void
  isAllSelected = false,
  
  // Azioni multiple
  onDeleteMultiple, // Funzione (ids) => Promise
  deleteConfirmMessage, // String o funzione (count) => string
  extraStickyActions = null, // Contenuto extra per azioni nella sticky bar (es. pulsante Modifica)
  
  // Extra custom content
  extraToolbarContent = null, // Contenuto extra dopo la search bar
}) => {
  
  const handleDeleteMultiple = async () => {
    const message = typeof deleteConfirmMessage === 'function'
      ? deleteConfirmMessage(selectedIds.length)
      : deleteConfirmMessage || `Eliminare ${selectedIds.length} ${selectedIds.length === 1 ? entityName : entityNamePlural}?`;
    
    if (window.confirm(message)) {
      try {
        await onDeleteMultiple(selectedIds);
      } catch (err) {
        console.error('Errore nella eliminazione multipla:', err);
        alert('Errore durante l\'eliminazione');
      }
    }
  };

  return (
    <div style={{ overflowX: 'hidden', width: '100%' }}>
      {/* Toolbar - Pulsanti Principali */}
      <div className="d-flex mb-3" style={{ gap: '8px' }}>
        {createPath && (
          <Button 
            as={Link} 
            to={createPath} 
            className="flex-grow-1" 
            style={{
              background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
              border: 'none',
              borderRadius: '12px',
              fontWeight: '600',
              fontSize: '0.95rem',
              padding: '12px',
              boxShadow: '0 4px 6px rgba(40, 167, 69, 0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              flex: actionsDropdownContent ? '0 0 70%' : '1'
            }}
          >
            <FontAwesomeIcon icon={createButtonIcon} />
            {createButtonText}
          </Button>
        )}
        
        {actionsDropdownContent && (
          <Dropdown style={{ flex: createPath ? '0 0 28%' : '1' }}>
            <Dropdown.Toggle 
              variant="primary" 
              style={{
                background: 'linear-gradient(135deg, #007bff 0%, #6610f2 100%)',
                border: 'none',
                borderRadius: '12px',
                fontWeight: '600',
                fontSize: '0.95rem',
                padding: '12px',
                width: '100%',
                boxShadow: '0 4px 6px rgba(0, 123, 255, 0.3)'
              }}
            >
              <FontAwesomeIcon icon={faCog} />
            </Dropdown.Toggle>
            <Dropdown.Menu 
              align="end" 
              style={{ 
                borderRadius: '12px', 
                boxShadow: '0 10px 25px rgba(0,0,0,0.15)', 
                minWidth: '200px' 
              }}
            >
              {actionsDropdownContent}
            </Dropdown.Menu>
          </Dropdown>
        )}
      </div>

      {/* Filtri Rapidi (opzionali) - griglia 2 per riga su mobile */}
      {quickFilters && quickFilters.length > 0 && (
        <div
          className="mb-3"
          style={{
            display: 'grid',
            gridTemplateColumns: quickFilters.length <= 2 ? `repeat(${quickFilters.length}, 1fr)` : 'repeat(2, 1fr)',
            gap: '8px',
          }}
        >
          {quickFilters.map((filter, index) => (
            <Button
              key={index}
              variant={filter.variant || 'outline-secondary'}
              size="sm"
              onClick={filter.onClick}
              active={filter.isActive}
              style={{
                borderRadius: '10px',
                fontWeight: '500',
                fontSize: '0.82rem',
                padding: '8px 4px',
                border: '2px solid',
                background: filter.isActive 
                  ? (filter.gradientActive || 'linear-gradient(135deg, #007bff 0%, #0056b3 100%)')
                  : 'white',
                color: filter.isActive ? 'white' : (filter.color || '#6c757d'),
                borderColor: filter.isActive 
                  ? (filter.borderColorActive || '#007bff')
                  : '#dee2e6',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {filter.icon && <FontAwesomeIcon icon={filter.icon} className="me-1" />}
              {filter.label}
            </Button>
          ))}
        </div>
      )}

      {/* Search Bar */}
      {onSearchChange && (
        <InputGroup className="mb-3" style={{ borderRadius: '12px', overflow: 'hidden' }}>
          <InputGroup.Text style={{ background: '#f8f9fa', border: 'none' }}>
            <FontAwesomeIcon icon={faSearch} />
          </InputGroup.Text>
          <Form.Control
            placeholder={searchPlaceholder}
            value={searchValue}
            onChange={onSearchChange}
            style={{ border: 'none', outline: 'none', boxShadow: 'none' }}
          />
          {activeFiltersCount > 0 && (
            <InputGroup.Text style={{ background: '#007bff', color: 'white', border: 'none', fontWeight: 'bold' }}>
              {activeFiltersCount}
            </InputGroup.Text>
          )}
        </InputGroup>
      )}

      {/* Extra Toolbar Content */}
      {extraToolbarContent && (
        <div className="mb-3">
          {extraToolbarContent}
        </div>
      )}

      {/* Card List */}
      <div>
        {items.map((item, index) => {
          const isSelected = selectedIds.includes(item.id);
          
          return (
            <Card 
              key={item.id || index} 
              className="mb-3" 
              style={{ 
                borderRadius: '12px',
                border: isSelected ? '2px solid #007bff' : '1px solid #dee2e6',
                boxShadow: isSelected 
                  ? '0 4px 12px rgba(0,123,255,0.2)' 
                  : '0 2px 4px rgba(0,0,0,0.05)',
                cursor: 'pointer'
              }}
              onClick={() => onCardClick && onCardClick(item)}
            >
              <Card.Body className="p-3">
                {renderCard(item, isSelected, (e) => {
                  e.stopPropagation();
                  onToggleSelect && onToggleSelect(item.id);
                })}
              </Card.Body>
            </Card>
          );
        })}
      </div>

      {/* Sticky Action Bar - Selezioni Multiple */}
      {selectedIds.length > 0 && onDeleteMultiple && (
        <div style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          background: 'white',
          borderTop: '2px solid #007bff',
          padding: '12px 16px',
          zIndex: 1000,
          boxShadow: '0 -4px 12px rgba(0,0,0,0.15)'
        }}>
          <div className="d-flex justify-content-between align-items-center">
            <span style={{ fontWeight: 'bold', fontSize: '0.95rem' }}>
              {selectedIds.length} {selectedIds.length === 1 ? entityName : entityNamePlural} selezionat{selectedIds.length === 1 ? 'o' : 'i'}
            </span>
            <div className="d-flex gap-2">
              {extraStickyActions}
              <Button 
                variant="outline-danger" 
                size="sm"
                onClick={handleDeleteMultiple}
                style={{ borderRadius: '8px', fontWeight: '600' }}
              >
                Elimina
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileListLayout;
